export interface RecordedPerformanceEvent {
  type: "noteon" | "noteoff" | "pedal";
  atMs: number;
  midi?: number;
  velocity?: number;
  down?: boolean;
  source?: string;
}

export interface RecordedPerformance {
  title: string;
  startedAtIso: string;
  durationMs: number;
  events: RecordedPerformanceEvent[];
}

/** Lightweight cross-platform MIDI-performance recorder (not microphone audio). */
export class PerformanceRecorder {
  private title = "";
  private startedAt = 0;
  private startedAtIso = "";
  private events: RecordedPerformanceEvent[] = [];
  private last: RecordedPerformance | null = null;

  start(title: string): void {
    this.title = title;
    this.startedAt = performance.now();
    this.startedAtIso = new Date().toISOString();
    this.events = [];
  }

  get isRecording(): boolean {
    return this.startedAt > 0;
  }

  note(
    type: "noteon" | "noteoff",
    midi: number,
    velocity: number,
    source: string,
  ): void {
    if (!this.isRecording) return;
    this.events.push({
      type,
      atMs: performance.now() - this.startedAt,
      midi,
      velocity,
      source,
    });
  }

  pedal(down: boolean): void {
    if (!this.isRecording) return;
    this.events.push({
      type: "pedal",
      atMs: performance.now() - this.startedAt,
      down,
    });
  }

  stop(): RecordedPerformance | null {
    if (!this.isRecording) return this.last;
    this.last = {
      title: this.title,
      startedAtIso: this.startedAtIso,
      durationMs: performance.now() - this.startedAt,
      events: [...this.events],
    };
    this.startedAt = 0;
    this.events = [];
    return this.last;
  }

  getLast(): RecordedPerformance | null {
    return this.last
      ? { ...this.last, events: this.last.events.map((event) => ({ ...event })) }
      : null;
  }
}
